// docs/72 IT16 — a catalogue piece in plain words: what it is, where it is worn, the numbers it adds,
// every power, the active, the use (a fight cast or a camp boon) and the boons themselves. The one
// place the client writes item text: the details card, tooltips, the shop, the Spoils and the item
// book all read it. Pure; covered by describe.test.ts.
import type { AbilitySpec, AreaSpec, CampSpec, CatalogueItem, DmgSpec, EffectSpec, Family, ItemSetSpec, ItemStats, PowerSpec } from './catalogue';
import { campUse, catalogueItem, fightUse, setOf } from './catalogue';

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
const cap = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
const words = (s: string): string => s.replace(/_/g, ' ');
// The catalogue's JSON values are checked by tools/items/check.mjs; read them loosely here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loose = any;

export const RARITY_WORDS = ['', 'Common', 'Uncommon', 'Rare', 'Legendary'] as const;
export const rarityWord = (r: number, relic = false): string => (relic ? 'Relic' : RARITY_WORDS[r] ?? 'Common');

const FAMILY_WORDS: Record<Family, string> = {
  sword: 'Sword', axe: 'Axe', mace: 'Mace', dagger: 'Dagger', spear: 'Spear', bow: 'Bow', crossbow: 'Crossbow', staff: 'Staff', wand: 'Wand',
  shield: 'Shield', focus: 'Focus', armour: 'Armour', helmet: 'Helm', cape: 'Cape', gloves: 'Gloves', boots: 'Boots', legs: 'Legs', belt: 'Belt', neck: 'Neckwear', ring: 'Ring', charm: 'Charm',
  potion: 'Potion', oil: 'Oil', scroll: 'Scroll', bomb: 'Bomb', food: 'Food', tool: 'Tool', tome: 'Tome', treasure: 'Treasure', curio: 'Curio',
};
export const familyWord = (f: string): string => FAMILY_WORDS[f as Family] ?? cap(f);

const SLOT_WORDS: Record<string, string> = { MainHand: 'Main hand', OffHand: 'Off hand', Torso: 'Body', Head: 'Head', Cape: 'Back', Trinket: 'Ring or charm', Hands: 'Hands', Feet: 'Feet', Neck: 'Neck', Trinket2: 'Ring or charm', Legs: 'Legs', Waist: 'Waist' };
const SHIELD_SIZES = ['buckler', 'round shield', 'kite shield', 'tower shield'];

/** "Sword · Main hand · two-handed", "Armour (chain) · Body", "Potion · used in a fight". */
export function kindLine(it: CatalogueItem): string {
  let fam = familyWord(it.family);
  if (it.family === 'shield' && it.size !== undefined) fam = cap(SHIELD_SIZES[it.size] ?? 'shield');
  else if (it.weight) fam = `${fam} (${it.weight})`;
  const parts = [fam];
  if (it.slot) parts.push(it.family === 'dagger' ? 'Either hand' : SLOT_WORDS[it.slot] ?? it.slot);
  if (it.two_handed) parts.push('two-handed');
  const u = it.use;
  if (u && 'fight' in u) parts.push('used up in a fight');
  else if (u && 'camp' in u) parts.push('used up in camp');
  else if (it.family === 'treasure') parts.push('sells for its full price');
  else if (!it.slot) parts.push('carried');
  return parts.join(' · ');
}

export const classesLine = (it: Pick<CatalogueItem, 'classes'>): string => (!it.classes?.length ? 'Any class' : it.classes.map(cap).join(', '));

/** The worn numbers, one line each, in the sheet's order. */
export function statLines(s: ItemStats | undefined, family?: string): string[] {
  if (!s) return [];
  const out: string[] = [];
  if (s.base_dmg) out.push(`${s.base_dmg} weapon damage`);
  if (s.range) out.push(`Shoots ${s.min_range ? `${s.min_range}–` : ''}${s.range} squares`);
  else if (s.reach && s.reach > 1) out.push(`Reach ${s.reach}`);
  else if (s.base_dmg && family && !['bow', 'crossbow', 'wand'].includes(family)) out.push('Melee: reach 1');
  if (s.pierce) out.push(`Ranged blows ignore ${s.pierce} guard`);
  if (s.guard) out.push(`${signed(s.guard)} guard`);
  if (s.hp) out.push(`${signed(s.hp)} max HP`);
  if (s.move) out.push(`${signed(s.move)} move`);
  if (s.power) out.push(`${signed(s.power)} power (weapon damage)`);
  if (s.focus) out.push(`${signed(s.focus)} focus (spells and heals)`);
  if (s.resource) out.push(`${signed(s.resource)} class resource`);
  if (s.push_resist) out.push(`${signed(s.push_resist)} against being pushed`);
  return out;
}

/** A status with its numbers: "burning 2 for 2 turns", "hidden until it acts". */
export function statusPhrase(kind: string, magnitude?: number, pulses?: number): string {
  const k = kind.toLowerCase();
  if (k === 'hidden') return pulses ? `hidden for ${plural(pulses, 'turn')}` : 'hidden until it acts';
  const flat = ['rooted', 'staggered', 'stuck'].includes(k);
  const m = !flat && magnitude ? ` ${magnitude}` : '';
  const p = pulses ? ` for ${plural(pulses, 'turn')}` : '';
  return `${k}${m}${p}`;
}

const FOES: Record<string, string> = { undead: 'the undead', beast: 'beasts', goblinkin: 'goblinkin', folk: 'folk (bandits, cultists)', brute: 'brutes (trolls)', scaled: 'the scaled (lizardfolk)', ooze: 'oozes', leader: 'named foes and leaders' };
const RESISTS: Record<string, string> = { melee: 'melee blows', ranged: 'ranged blows', spell: 'spells', area: 'blasts and areas', fire: 'fire and burning', trap: 'traps' };
const GROUNDS: Record<string, string> = { rough: 'rough ground', mud: 'mud', sand: 'sand', snow: 'snow', thicket: 'thicket', shallows: 'shallows', ice: 'ice' };
const TRAITS: Record<string, string> = {
  skirmisher: 'Skirmisher: ignores zones of control',
  slippery: 'Slippery: breaks away from a foe beside you for free',
  sure_footed: 'Sure-footed: weather and season never slow you',
  darkvision: 'Darkvision: sees in the dark',
  keen_eyed: 'Keen-eyed: sees 2 squares further through fog and storm',
  watchful: 'Watchful: sees 1 square further',
  lightbearer: 'Lightbearer: carries light in the dark',
  laden: 'Laden: heavy going through rough ground',
};
export function traitWords(t: string): string {
  if (t.startsWith('stride:')) return `Strides through ${GROUNDS[t.slice(7)] ?? t.slice(7)} at one pace`;
  return TRAITS[t] ?? cap(words(t));
}

const snake = (s: string): string => s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/** docs/95: a `fk_grammar::Power` as serde writes it (`{ FirstBlow: 2 }`, `{ Resist: { from: 'Fire', amount: 2 } }`)
 *  in the authoring shape `powerLine` reads (`{ first_blow: 2 }`). */
export function rustPower(p: unknown): PowerSpec {
  const [k, v] = typeof p === 'string' ? [p, true] : (Object.entries(p as Record<string, unknown>)[0] ?? ['power', 0]);
  const low = (x: unknown): unknown => (typeof x === 'string' ? snake(x) : x);
  const val = v && typeof v === 'object'
    ? Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([a, b]) => [a === 'kind' ? 'status' : a, low(b)]))
    : low(v);
  return { [snake(k)]: val } as PowerSpec;
}

/** One power in plain words. */
export function powerLine(p: PowerSpec): string {
  const [k] = Object.keys(p);
  const v = p[k!] as Loose;
  const o = v as Loose;
  switch (k) {
    case 'on_hit': return `Every blow that deals damage leaves the target ${statusPhrase(o.status, o.magnitude, o.pulses)}`;
    case 'bane': return `+${o.dmg} damage against ${FOES[o.foe] ?? o.foe}`;
    case 'drain': return `Heals you ${v} whenever your weapon blow deals damage`;
    case 'thorns': return `A foe that strikes you in melee takes ${v} damage`;
    case 'resist': return `Takes ${o.amount} less damage from ${RESISTS[o.from] ?? o.from}`;
    case 'immune': return `Never ${String(v).toLowerCase()}`;
    case 'opening': return `Starts every fight ${statusPhrase(o.status, o.magnitude, o.status === 'hidden' ? 0 : o.pulses)}`;
    case 'mend': return `Heals ${v} at the end of each turn while standing`;
    case 'wellspring': return `+${v} class resource at the end of each turn`;
    case 'last_stand': return `Once a fight, a blow that would drop you leaves you at ${v} HP`;
    case 'reaper': {
      const bits = [o.heal ? `heal ${o.heal}` : '', o.resource ? `gain ${o.resource} resource` : ''].filter(Boolean);
      return `When your blow fells a foe, ${bits.join(' and ') || 'nothing more'}`;
    }
    case 'aura': return `Friends within ${plural(o.radius, 'square')} are ${statusPhrase(o.status, o.magnitude)} each turn`;
    case 'execute': return `+${v} damage to a foe at or below half its HP`;
    case 'first_blow': return `+${v} damage on your first damaging blow of each fight`;
    case 'trait': return traitWords(String(v));
    case 'purse': return `${v}% more gold from every fight`;
    case 'lore': return `${v}% more experience`;
    case 'haggle': return `Village prices ${v}% better for you, buying and selling`;
    case 'insight': return `Puzzle hints cost the party ${v} less experience while you are deployed`;
    case 'keen_search': return `Your Search reaches ${plural(Number(v), 'square')} further`;
    case 'carried': return o.pack ? `Carried: the pack holds ${o.pack} more` : `Carried: stashes you open give ${plural(o.lucky, 'extra draw')}`;
    // docs/110 SE3: the powers only a whole set grants
    case 'cleave': return `Cleave: every melee blow that lands also deals ${v} to each other foe beside you`;
    case 'ricochet': return `Ricochet: every shot that lands also deals ${v} to the nearest other foe within 2 squares of it`;
    case 'shatter': return `Shatter: a foe you fell bursts, dealing ${v} to every foe beside it`;
    case 'rally': return `Rally: when you fell a foe, you and friends within 2 squares heal ${v}`;
    case 'fury': return `Fury: +${v} damage on your blows while you are at or below half HP`;
    case 'second_wind': return `Second wind: once a fight, the first blow that leaves you at half HP or less heals you ${v}`;
    case 'avenger': return `Avenger: when a friend falls, you are blessed ${v} for 2 turns`;
    case 'bulwark': return `Bulwark: friends beside you take ${v} less from every blow`;
    case 'pack_hunter': return `Pack hunter: +${v} damage against a foe another friend stands beside`;
    case 'spellsurge': return `Spellsurge: +${v} damage on your spells and blasts`;
    default: return cap(words(k ?? 'power'));
  }
}

const TO: Record<string, string> = { enemies: 'foes', allies: 'friends', all: 'everyone', caster: 'you' };
function dmgPhrase(d: DmgSpec): string {
  if (d === 'weapon') return 'your weapon damage';
  if ('spell' in d) return `${d.spell} + focus damage`;
  if ('flat' in d) return `${d.flat} damage`;
  return d.weapon_plus ? `weapon damage +${d.weapon_plus}` : 'your weapon damage';
}

/** One ability effect in plain words. `used` = a used-up item (a draught heals what it says). */
export function effectLine(e: EffectSpec, opts: { used?: boolean; single?: boolean; lands?: boolean } = {}): string {
  const [k] = Object.keys(e);
  const v = e[k!] as Loose;
  // docs/63 RF7: a teleport's gift to friends reaches those beside where you land, not you
  const to = (t: unknown) => (opts.lands && t === 'allies' ? 'each friend beside where you land' : opts.single && t !== 'caster' ? 'the target' : TO[String(t)] ?? String(t));
  switch (k) {
    case 'damage': return `${cap(dmgPhrase(v.dmg as DmgSpec))} to ${to(v.to)}`;
    case 'heal': return `Heals ${to(v.to)} ${v.amount}${opts.used ? '' : ' + focus'}`;
    case 'push': return `Pushes ${plural(Number(v), 'square')} away`;
    case 'pull': return `Pulls ${plural(Number(v), 'square')} closer`;
    case 'status': return `${cap(to(v.to))}: ${statusPhrase(String(v.kind), Number(v.magnitude), Number(v.pulses))}`;
    case 'clear': return `Clears ${String(v.kind)} from ${to(v.to)}`;
    case 'cleanse': return `Clears every harmful status from ${to(v)}`;
    case 'teleport': return `You step to a free square within ${Number(v)}`;
    case 'move_bonus': return `+${plural(Number(v), 'square')} of move this turn`;
    case 'charge': return `You run up to ${plural(Number(v), 'square')} at the target`;
    case 'hazard': return `Sets the ground alight: burning ${v.magnitude} for ${plural(Number(v.pulses), 'turn')}`;
    case 'trap': return `A hidden snare: ${v.dmg} damage, rooted ${plural(Number(v.root_pulses), 'turn')} (lasts ${plural(Number(v.pulses), 'turn')})`;
    case 'revive': return `Stands a downed friend up with ${v.hp} HP`;
    case 'resource': return `+${Number(v)} class resource`;
    case 'reveal': return 'Every hidden thing and trap on the field shows itself';
    default: return cap(words(k ?? 'effect'));
  }
}

export function areaPhrase(a: AreaSpec | undefined): string {
  const area = a ?? 'single';
  if (area === 'single') return 'one target';
  if (area === 'self') return 'yourself';
  if (area === 'adjacent') return 'every square around you';
  if ('blast' in area) return `a blast of radius ${area.blast}`;
  if ('line' in area) return `a line of ${area.line}`;
  return `friends within ${area.allies_within}`;
}

export function rangePhrase(a: AbilitySpec): string {
  const r = a.range;
  if (r === 'self' || a.area === 'self') return 'on yourself';
  if (r === 'reach') return 'a friend or foe beside you';
  if (r === 'weapon') return 'at weapon range';
  if (r === 0) return 'around you';
  return `range ${r}${a.los === false ? '' : ', needs a clear line'}`;
}

export interface AbilityWords { name: string; head: string; effects: string[] }
/** An active or a fight use: "Kindle · Action · range 5 · a blast of radius 1 · every 3 turns". */
export function abilityWords(a: AbilitySpec, used = false): AbilityWords {
  const single = (a.area ?? 'single') === 'single' && a.range !== 'self';
  const lands = a.effects.some((e) => 'teleport' in e);
  const bits = [used ? 'Bonus action' : a.slot === 'bonus' ? 'Bonus action' : 'Action', rangePhrase(a)];
  if (a.area && a.area !== 'single' && a.area !== 'self') bits.push(areaPhrase(a.area));
  if (!used && a.cooldown) bits.push(`ready again after ${plural(a.cooldown, 'turn')}`);
  if (a.cost) bits.push(`costs ${a.cost} resource`);
  if (used) bits.push('used up');
  return { name: a.name, head: bits.join(' · '), effects: a.effects.map((e) => effectLine(e, { used, single, lands })) };
}

/** fk_grammar `BOONS`: name and words by card. */
export const BOONS: Readonly<Record<number, { name: string; text: string }>> = {
  1001: { name: 'Well fed', text: '+3 max HP' }, 1002: { name: 'Honed edge', text: '+1 power' }, 1003: { name: 'Clear head', text: '+1 focus' },
  1004: { name: 'Braced', text: '+1 guard' }, 1005: { name: 'Light step', text: '+1 move' }, 1006: { name: 'Quick', text: '+2 damage on the first blow' },
  1007: { name: 'Deep reserves', text: '+1 max resource' }, 1008: { name: 'Hearty', text: '+6 max HP' }, 1009: { name: 'Keen edge', text: '+2 power' },
  1010: { name: 'Iron skin', text: '+2 guard' }, 1011: { name: 'Ambusher', text: 'The party starts hidden' }, 1012: { name: 'Scouted', text: 'Foes start marked 1 for 2 turns' },
  1013: { name: 'Warhorn', text: 'Foes start staggered for 1 turn' }, 1014: { name: 'Blessed start', text: 'Starts blessed 1 for 2 turns' }, 1015: { name: 'Warded start', text: 'Starts warded 3' },
  1016: { name: 'Fireproofed', text: 'Never burns' }, 1017: { name: 'Sure feet', text: 'Weather and season never slow' }, 1018: { name: 'Night eyes', text: 'Darkvision, and sees one further' },
  1019: { name: 'Venomed blade', text: 'Blows lay bleeding 1 for 2 turns' }, 1020: { name: 'Lucky', text: 'Stashes give one more draw' },
};
const CARDS: Record<number, string> = { 1: 'Hardy: +3 HP', 2: 'Guard +1', 3: 'Power +1', 4: 'Focus +1', 5: 'Swift: +1 move' };

/** What a camp use does, in words. */
export function campLine(c: CampSpec): string {
  if ('boon' in c) {
    const b = BOONS[c.boon.card];
    const who = c.boon.party ? 'every hero in the party' : 'this hero';
    return `${b ? `${b.name} (${b.text})` : `Boon ${c.boon.card}`} on ${who} for the next ${plural(c.boon.fights, 'fight')}`;
  }
  if ('xp' in c) return `The hero learns ${c.xp} experience`;
  if ('gold' in c) return `Opened for ${c.gold} gold`;
  if ('card' in c) return `For good: ${CARDS[c.card] ?? `card ${c.card}`}`;
  return 'In a puzzle: the party’s next hint is free';
}

/** The verb on the Use button for a camp use. */
export function campVerb(it: CatalogueItem): string {
  switch (it.family) {
    case 'food': return 'Eat';
    case 'potion': return 'Drink';
    case 'tome': case 'scroll': return 'Read';
    case 'oil': return 'Apply';
    default: return 'Use';
  }
}

export interface ItemWords {
  name: string;
  rarity: string;
  kind: string;
  classes: string;
  worn: string[];
  powers: string[];
  active?: AbilityWords;
  use?: { when: 'fight'; ability: AbilityWords } | { when: 'camp'; line: string; verb: string };
  flavour: string;
  source: string;
  price?: number;
  relic: boolean;
}

/** Everything the details card says about a piece. */
export function describeItem(it: CatalogueItem): ItemWords {
  const f = fightUse(it), c = campUse(it);
  const out: ItemWords = {
    name: it.name, rarity: rarityWord(it.rarity, it.relic), kind: kindLine(it), classes: classesLine(it),
    worn: statLines(it.stats, it.family), powers: (it.powers ?? []).map(powerLine), flavour: it.flavour, source: it.source, relic: !!it.relic,
  };
  if (it.price) out.price = it.price;
  if (it.active) out.active = abilityWords(it.active);
  if (f) out.use = { when: 'fight', ability: abilityWords(f, true) };
  else if (c) out.use = { when: 'camp', line: campLine(c), verb: campVerb(it) };
  if (it.family === 'treasure' && !out.powers.length) out.powers.push('Sells for its full price in any village');
  return out;
}

/** One short line for a tooltip or a list row: the first thing it does. */
export function shortLine(it: CatalogueItem): string {
  const d = describeItem(it);
  if (d.use?.when === 'fight') return d.use.ability.effects[0] ?? d.use.ability.name;
  if (d.use?.when === 'camp') return d.use.line;
  return [...d.worn.slice(0, 2), ...d.powers.slice(0, 1)].join(' · ') || d.kind;
}

const IMMUNE_TO = ['guarded', 'warded', 'blessed', 'marked', 'hidden', 'slowed', 'rooted', 'burning', 'weakened', 'staggered', 'stuck', 'bleeding'];
/** docs/72 IT3: the short tag a fired worn power rises with over its wearer (fk_sim `GearPower`). */
export function gearPowerTag(power: string, amount: number): string {
  switch (power) {
    case 'LastStand': return 'Last stand!';
    case 'Thorns': return `Thorns ${amount}`;
    case 'Drain': return `Drain +${amount}`;
    case 'Bane': return `Bane +${amount}`;
    case 'Execute': return `Execute +${amount}`;
    case 'FirstBlow': return `First blow +${amount}`;
    case 'Resist': return `Resist −${amount}`;
    case 'Immune': return IMMUNE_TO[amount] ? `Immune: ${IMMUNE_TO[amount]}` : 'Immune';
    case 'Reaper': return `Reaper +${amount}`;
    case 'Mend': return `Mend +${amount}`;
    case 'Wellspring': return `Wellspring +${amount}`;
    case 'Cleave': return `Cleave ${amount}`;
    case 'Ricochet': return `Ricochet ${amount}`;
    case 'Shatter': return `Shatter ${amount}`;
    case 'Rally': return `Rally +${amount}`;
    case 'Fury': return `Fury +${amount}`;
    case 'SecondWind': return `Second wind +${amount}`;
    case 'Avenger': return `Avenger! Blessed ${amount}`;
    case 'Bulwark': return `Bulwark −${amount}`;
    case 'PackHunter': return `Pack hunter +${amount}`;
    case 'Spellsurge': return `Spellsurge +${amount}`;
    default: return power.replace(/([a-z])([A-Z])/g, '$1 $2');
  }
}

/** docs/110: a set's full-set bonus in words: its numbers and powers, one line each, and its ability. */
export interface SetWords { name: string; bonus: string[]; ability: AbilityWords; pieces: { def: number; name: string }[] }
export function describeSet(set: ItemSetSpec): SetWords {
  return {
    name: set.name,
    bonus: [...statLines(set.bonus.stats), ...(set.bonus.powers ?? []).map(powerLine)],
    ability: abilityWords(set.bonus.active),
    pieces: set.members.map((def) => ({ def, name: catalogueItem(def)?.name ?? `#${def}` })),
  };
}

/** What a hero has of a piece's set: which of its pieces they own (worn or in the pack) and wear. */
export interface SetHolding { owned: ReadonlySet<number>; worn: ReadonlySet<number> }
export interface SetProgress {
  set: ItemSetSpec;
  /** Distinct pieces of the set the hero owns, worn or in the pack. */
  owned: number;
  total: number;
  /** Every piece worn at once: the bonus is on. */
  complete: boolean;
}
/** docs/110 SE4: how far a hero is with the set a piece belongs to (undefined if it has none). */
export function setProgress(def: number, holding: SetHolding): SetProgress | undefined {
  const set = setOf(def);
  if (!set) return undefined;
  return {
    set,
    owned: set.members.filter((m) => holding.owned.has(m)).length,
    total: set.members.length,
    complete: set.members.every((m) => holding.worn.has(m)),
  };
}
